/**
 * Modal "Solicitar recurso" — Sprint 17 S17-16.
 *
 * El vecino abre desde /vecino/mis-datos (tab "Mis datos", sección
 * "Recursos asignados") para pedir un llavero o control remoto.
 *
 * Form:
 * - Tipo: LL / CR. Para CR se exige seleccionar vehículo del lote.
 * - Motivo (enum D-S17-9): perdida / robo / deterioro / falla_fabrica /
 *   nuevo_residente / nuevo_vehiculo / otro.
 * - motivo_texto obligatorio si motivo='otro'.
 * - Residente del lote a quien va dirigido (selector entre residentes
 *   aprobados de la casa).
 * - Notas libre (opcional).
 *
 * Submit → POST /me/solicitudes-recurso. La solicitud nace en
 * `pendiente_aprobacion`; la comisión la revisa desde
 * `/admin/validaciones` (S17-20 Fase 4).
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { crearSolicitudRecursoVecino } from '../../lib/api/solicitudes-recurso';
import type { ResidenteConPersonaRead } from '../../lib/api/residentes';
import type {
  MotivoSolicitud,
  RecursoConSuspensionRead,
  SolicitudRecursoCreate,
  TipoSolicitudRecurso,
  VehiculoRead,
} from '../../lib/api/types';
import {
  residentesConLlaveroVivo,
  vehiculosConCrVivo,
} from '../../lib/recursos';
import { Button } from '../ui/Button';

interface Props {
  loteId: string;
  residentes: ResidenteConPersonaRead[];
  vehiculos: VehiculoRead[];
  /** Recursos de la casa — para descontar a quien ya tiene (Sprint 22). */
  recursos: RecursoConSuspensionRead[];
  onCerrar: () => void;
  onCreada: () => void;
}

const MOTIVOS: { value: MotivoSolicitud; label: string; cobra: boolean }[] = [
  { value: 'perdida', label: 'Pérdida', cobra: true },
  { value: 'robo', label: 'Robo', cobra: true },
  { value: 'deterioro', label: 'Deterioro', cobra: true },
  { value: 'falla_fabrica', label: 'Falla de fábrica (sin costo)', cobra: false },
  { value: 'nuevo_residente', label: 'Nuevo residente', cobra: true },
  { value: 'nuevo_vehiculo', label: 'Nuevo vehículo', cobra: true },
  { value: 'otro', label: 'Otro', cobra: true },
];

export function ModalSolicitarRecurso({
  loteId,
  residentes,
  vehiculos,
  recursos,
  onCerrar,
  onCreada,
}: Props) {
  const [tipo, setTipo] = useState<TipoSolicitudRecurso>('llavero');
  const [motivo, setMotivo] = useState<MotivoSolicitud>('nuevo_residente');
  const [motivoTexto, setMotivoTexto] = useState('');
  const [residenteIdElegido, setResidenteId] = useState('');
  const [vehiculoIdElegido, setVehiculoId] = useState('');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const motivoActual = MOTIVOS.find((m) => m.value === motivo);

  // Sprint 22 — quien ya tiene no aparece como candidato. Cada persona lleva
  // un llavero y cada auto un control; pedir para alguien que ya tiene sería
  // un 422 del backend. Ojo con la asimetría: para un CONTROL no se descuenta
  // a la persona (alguien con dos autos lleva dos controles), se descuenta el
  // auto. Para un LLAVERO se descuenta a la persona.
  const conLlavero = residentesConLlaveroVivo(recursos);
  const conControl = vehiculosConCrVivo(recursos);
  const residentesDisponibles =
    tipo === 'llavero'
      ? residentes.filter((r) => !conLlavero.has(r.id))
      : residentes;
  const vehiculosDisponibles = vehiculos.filter((v) => !conControl.has(v.id));

  // Si al cambiar de tipo el elegido dejó de estar disponible, la selección
  // se considera vacía (en vez de mandar al backend un id ya ocupado).
  const residenteId = residentesDisponibles.some(
    (r) => r.id === residenteIdElegido,
  )
    ? residenteIdElegido
    : residentesDisponibles.length === 1
      ? residentesDisponibles[0]!.id
      : '';
  const vehiculoId = vehiculosDisponibles.some(
    (v) => v.id === vehiculoIdElegido,
  )
    ? vehiculoIdElegido
    : vehiculosDisponibles.length === 1
      ? vehiculosDisponibles[0]!.id
      : '';

  const formValido =
    residenteId !== '' &&
    (tipo !== 'control_remoto' || vehiculoId !== '') &&
    (motivo !== 'otro' || motivoTexto.trim().length > 0);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formValido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: SolicitudRecursoCreate = {
        lote_id: loteId,
        residente_id: residenteId,
        vehiculo_id: tipo === 'control_remoto' ? vehiculoId : null,
        tipo,
        motivo,
        motivo_texto: motivo === 'otro' ? motivoTexto.trim() : null,
        notas: notas.trim() || null,
      };
      await crearSolicitudRecursoVecino(payload);
      onCreada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Solicitar recurso"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Solicitar recurso
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="bg-orange/[0.08] border-l-4 border-orange p-3 rounded-sm text-xs text-text-secondary">
          Tu solicitud llega a la Comisión para revisión. Si la aprueban,
          se te enviará el detalle para el pago{' '}
          {motivoActual?.cobra === false ? (
            <strong>(no aplica en este motivo)</strong>
          ) : (
            'antes de retirar el recurso'
          )}
          .
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Tipo de recurso</span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoSolicitudRecurso)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          >
            <option value="llavero">Llavero</option>
            <option value="control_remoto">Control remoto</option>
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Residente <span className="text-state-danger">*</span>
          </span>
          <select
            value={residenteId}
            onChange={(e) => setResidenteId(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          >
            <option value="">— elige el residente —</option>
            {residentesDisponibles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.persona_nombre_completo}
                {r.parentesco !== 'titular' && ` (${r.parentesco})`}
              </option>
            ))}
          </select>
          {residentes.length === 0 ? (
            <span className="text-[11px] text-state-warning">
              No tienes residentes aprobados activos. Agrega uno desde la
              tab "Residentes" antes de solicitar un recurso.
            </span>
          ) : (
            residentesDisponibles.length === 0 && (
              <span className="text-[11px] text-state-warning">
                Todas las personas de tu casa ya tienen su llavero (cada
                persona lleva uno). Si a alguien se le perdió o le falló, usa
                "Reportar problema" en el llavero para pedir el reemplazo.
              </span>
            )
          )}
        </label>

        {tipo === 'control_remoto' && (
          <label className="text-sm space-y-1 block">
            <span className="text-text-secondary">
              Vehículo <span className="text-state-danger">*</span>
            </span>
            <select
              value={vehiculoId}
              onChange={(e) => setVehiculoId(e.target.value)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            >
              <option value="">— elige el vehículo —</option>
              {vehiculosDisponibles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.patente}
                  {v.marca && ` · ${v.marca}`}
                  {v.modelo && ` ${v.modelo}`}
                </option>
              ))}
            </select>
            {vehiculos.length === 0 ? (
              <span className="text-[11px] text-state-warning">
                No tienes vehículos registrados. Agrega uno desde la tab
                "Vehículos" antes de solicitar un control remoto.
              </span>
            ) : (
              vehiculosDisponibles.length === 0 && (
                <span className="text-[11px] text-state-warning">
                  Todos tus vehículos ya tienen su control remoto (cada auto
                  lleva uno). Si se perdió o falló, usa "Reportar problema" en
                  el control para pedir el reemplazo.
                </span>
              )
            )}
          </label>
        )}

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Motivo <span className="text-state-danger">*</span>
          </span>
          <select
            value={motivo}
            onChange={(e) => setMotivo(e.target.value as MotivoSolicitud)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          >
            {MOTIVOS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        {motivo === 'otro' && (
          <label className="text-sm space-y-1 block">
            <span className="text-text-secondary">
              Detalla el motivo <span className="text-state-danger">*</span>
            </span>
            <input
              type="text"
              value={motivoTexto}
              onChange={(e) => setMotivoTexto(e.target.value)}
              maxLength={255}
              placeholder="Ej: necesito control remoto adicional para una visita"
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            />
          </label>
        )}

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Notas (opcional)
          </span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            placeholder="Algún detalle que quieras agregar para la Comisión"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!formValido || submitting}
          >
            {submitting ? 'Enviando…' : 'Enviar solicitud'}
          </Button>
        </div>
      </form>
    </div>
  );
}
